// Display helpers: dates in the device's locale and time zone, sizes, senders, how a message arrived.

import type { Delivery } from "./api";

const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** List dates: the time today, the weekday this week, day and month this year, else the full date. */
export function listDate(iso: string | null, now = new Date()): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (sameDay(d, now)) return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const days = (now.getTime() - d.getTime()) / 86_400_000;
  if (days >= 0 && days < 6) return d.toLocaleDateString(undefined, { weekday: "short" });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function fullDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" });
}

export function relativeTime(iso: string | null, now = Date.now()): string {
  if (!iso) return "never";
  const seconds = Math.round((now - new Date(iso).getTime()) / 1000);
  if (seconds < 45) return "just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} h ago`;
  return listDate(iso);
}

/** Passes, events and contact cards: the files the phone hands to Wallet, Calendar or Contacts (the server's
 * handoff_type knows the same ones). */
const HANDOFF_TYPES = new Set(["application/vnd.apple.pkpass", "text/calendar", "application/ics", "text/vcard", "text/x-vcard"]);

/** How an attachment opens: images and PDFs in the app, plain text in a new tab, passes, events and contact cards in
 * the phone's own app, anything else as a download. A PDF, pass, event or card sent as application/octet-stream (some
 * mailers know no better) is known by its name. */
export function attachmentKind(mime: string, filename: string): "image" | "pdf" | "text" | "handoff" | "file" {
  const type = mime.toLowerCase();
  const unknown = !type || type === "application/octet-stream";
  if (type.startsWith("image/")) return "image";
  if (type === "application/pdf" || (unknown && /\.pdf$/i.test(filename))) return "pdf";
  if (type === "text/plain") return "text";
  if (HANDOFF_TYPES.has(type) || (unknown && /\.(pkpass|ics|vcf)$/i.test(filename))) return "handoff";
  return "file";
}

export function size(bytes: number | null | undefined): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

/** Lower case without accents (é is e), so typing finds names and words either way. */
export function fold(text: string): string {
  return text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
}

export function sender(name: string | null, addr: string | null): string {
  return (name && name.trim()) || addr || "(unknown sender)";
}

/** Whether "Sent to" tells more than the To and Cc lines: the message was forwarded, or the address it was sent to is
 * in neither (Bcc, a list, most spam). */
export function showDelivery(d: Delivery | null | undefined): d is Delivery {
  return !!d && d.path.length > 0 && (d.forwarded || !d.addressed);
}
